// Live Activity page. GET /api/v1/portfolio/activity[?…filters].
//   - filters + pagination round-trip to the server (no client-side slicing)
//   - filter dropdowns are populated from the response, not hardcoded
//   - search input debounced
//   - CSV export deferred until the async export endpoint lands

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ExportCsvLink, OwnershipPill, PageHeader, SortTable, TypePill, VenuePill } from '../../components/shell';
import { TxAttachments } from '../../components/TxAttachments';
import { fmtQty, fmtUSD } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePolling } from '../../lib/usePolling';

const ACTIVITY_POLL_MS = 60_000;

const VENUE_LABELS: Record<string, string> = {
  coinbase_exchange: 'Coinbase Exchange',
  onchain: 'On-chain',
  bybit: 'Bybit',
  kraken: 'Kraken',
};

const PAGE_SIZE = 25;

interface ActivityAccount {
  id: string; label: string; venue: string; ownership: string;
  chain?: string;  // venue=onchain only
}

const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  bsc: 'BNB Smart Chain',
};
function activityVenueLabel(venue: string, chain?: string): string {
  if (venue === 'onchain' && chain && CHAIN_LABELS[chain]) return CHAIN_LABELS[chain];
  return VENUE_LABELS[venue] || venue;
}

interface ActivityRow {
  id: string;
  occurred_at: string;
  account_id: string;
  tx_type: string;
  asset: string;
  quantity: number;
  price_usd: number | null;
  usd_value: number;
  venue_tx_id: string;
  notes: string;
}

interface ActivityPayload {
  results: ActivityRow[];
  accounts: ActivityAccount[];
  tx_types: string[];
  assets: string[];
  meta: { total: number; limit: number; offset: number };
}

// UI label for the TypePill stays simpler than the server enum.
function uiTxType(t: string): string {
  if (t === 'trade_buy') return 'buy';
  if (t === 'trade_sell') return 'sell';
  if (t === 'transfer_in' || t === 'transfer_out') return 'transfer';
  if (t === 'adjustment') return 'rebalance';
  return t;
}

export default function Activity() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [acct, setAcct] = useState<string>('all');
  const [type, setType] = useState<string>('all');
  const [asset, setAsset] = useState<string>('all');
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [page, setPage] = useState(0);
  // CR.g — per-transaction document attachments (manager/super-user surface).
  const [attachTx, setAttachTx] = useState<{ id: string; ref: string } | null>(null);
  const isStaff = user?.role === 'manager' || !!user?.is_superuser;

  // Manager-without-client_id → /clients (same pattern as the other pages).
  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  // Debounce the free-text search so we don't fire on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  // Reset to page 0 whenever any filter changes.
  useEffect(() => { setPage(0); }, [acct, type, asset, debouncedQuery]);

  // Build the URL once per filter/page change so the polling loop knows when
  // to restart with the new query.
  const url = useMemo(() => {
    const qs = new URLSearchParams();
    if (clientId) qs.set('client_id', clientId);
    if (acct !== 'all') qs.set('account_id', acct);
    if (type !== 'all') qs.set('tx_type', type);
    if (asset !== 'all') qs.set('asset', asset);
    if (debouncedQuery) qs.set('search', debouncedQuery);
    qs.set('limit', String(PAGE_SIZE));
    qs.set('offset', String(page * PAGE_SIZE));
    return `/portfolio/activity?${qs.toString()}`;
  }, [clientId, acct, type, asset, debouncedQuery, page]);

  const pollingEnabled = !!user && (user.role === 'client' || !!clientId);
  const { data, error } = usePolling<ActivityPayload>(
    (signal) => api<ActivityPayload>(url, { signal }),
    { intervalMs: ACTIVITY_POLL_MS, deps: [url], enabled: pollingEnabled },
  );

  useEffect(() => {
    if (!error) { setErrorMsg(null); return; }
    const status = (error as { status?: number })?.status;
    if (status === 404 && user?.role !== 'client') {
      navigate('/fund', { replace: true });
      return;
    }
    setErrorMsg(status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load activity.');
  }, [error, user, navigate]);

  if (errorMsg) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{errorMsg}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }

  const { results, accounts, tx_types, assets, meta } = data;
  const accountById = new Map(accounts.map(a => [a.id, a]));
  const pages = Math.max(1, Math.ceil(meta.total / PAGE_SIZE));

  return (
    <div className="view container clean-screen">
      <PageHeader
        kicker="ACTIVITY"
        title={<><em>All</em> transactions.</>}
        meta={
          <span className="label-s">
            {meta.total.toLocaleString()} RECORDS
            {(acct !== 'all' || type !== 'all' || asset !== 'all' || debouncedQuery) ? ' (FILTERED)' : ''}
          </span>
        }
      />

      <div className="filter-bar">
        <select className="filter-chip" value={acct} onChange={e => setAcct(e.target.value)}
                style={{ borderRight: '1px solid var(--rule)', appearance: 'none' }}>
          <option value="all">ALL ACCOUNTS</option>
          {accounts.map(a => (
            <option key={a.id} value={a.id}>{(a.label || a.id).toUpperCase()}</option>
          ))}
        </select>

        <select className="filter-chip" value={type} onChange={e => setType(e.target.value)}
                style={{ borderRight: '1px solid var(--rule)', borderLeft: 0, appearance: 'none' }}>
          <option value="all">ALL TYPES</option>
          {tx_types.map(t => (
            <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>
          ))}
        </select>

        <select className="filter-chip" value={asset} onChange={e => setAsset(e.target.value)}
                style={{ borderRight: '1px solid var(--rule)', borderLeft: 0, appearance: 'none' }}>
          <option value="all">ALL ASSETS</option>
          {assets.map(t => (
            <option key={t} value={t}>{t.toUpperCase()}</option>
          ))}
        </select>

        <input className="filter-search"
               placeholder="Search reference, asset, type…"
               value={query} onChange={e => setQuery(e.target.value)} />

        <button className="btn-ghost btn"
                onClick={() => { setType('all'); setAcct('all'); setAsset('all'); setQuery(''); }}>
          Reset
        </button>
        <ExportCsvLink endpoint="activity" clientId={clientId} params={{
          ...(acct !== 'all' ? { account_id: acct } : {}),
          ...(type !== 'all' ? { tx_type: type } : {}),
          ...(asset !== 'all' ? { asset } : {}),
          ...(debouncedQuery ? { search: debouncedQuery } : {}),
        }} />
      </div>

      <section className="clean-panel" style={{ marginTop: 32 }}>
        <SortTable
          rows={results}
          defaultSort="occurred_at"
          defaultDir="desc"
          emptyMessage="No transactions match the selected filters."
          columns={[
            {
              key: 'occurred_at', label: 'DATE / TIME',
              render: (r: any) => (
                <span className="num" style={{ color: 'var(--ink-2)', fontSize: 12 }}>
                  {new Date(r.occurred_at).toISOString().slice(0, 16).replace('T', ' ')}
                </span>
              ),
            },
            {
              key: 'account_id', label: 'ACCOUNT',
              render: (r: any) => {
                const a = accountById.get(r.account_id);
                if (!a) return null;
                const shortLabel = a.label.split(' · ')[1] || a.label || '—';
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="serif" style={{ fontSize: 14 }}>{shortLabel}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <VenuePill venue={activityVenueLabel(a.venue, a.chain) as any} />
                      {a.ownership === 'company_owned' && <OwnershipPill ownership={'company-held' as any} />}
                    </div>
                  </div>
                );
              },
            },
            { key: 'tx_type', label: 'TYPE', render: (r: any) => <TypePill type={uiTxType(r.tx_type)} /> },
            { key: 'asset', label: 'ASSET',
              render: (r: any) => <span className="serif" style={{ fontSize: 15 }}>{r.asset}</span> },
            {
              key: 'quantity', label: 'QUANTITY', align: 'right',
              render: (r: any) => (
                <span className="num">
                  {fmtQty(r.quantity, ['USD', 'USDC', 'USDT'].includes(r.asset) ? 2 : 4)}
                </span>
              ),
            },
            {
              key: 'price_usd', label: 'PRICE', align: 'right',
              render: (r: any) => (
                <span className="num">
                  {r.price_usd != null ? fmtUSD(r.price_usd, r.price_usd < 10 ? 4 : 2) : '—'}
                </span>
              ),
            },
            { key: 'usd_value', label: 'USD VALUE', align: 'right',
              render: (r: any) => <span className="num">{fmtUSD(r.usd_value)}</span> },
            { key: 'venue_tx_id', label: 'REFERENCE',
              render: (r: any) => <span className="num" style={{ color: 'var(--ink-3)', fontSize: 12 }}>{r.venue_tx_id || '—'}</span> },
            ...(isStaff ? [{
              key: 'docs', label: 'DOCS', align: 'right' as const,
              render: (r: any) => (
                <button type="button" className="btn-link"
                        onClick={() => setAttachTx({ id: r.id, ref: r.venue_tx_id || r.asset })}>
                  Docs
                </button>
              ),
            }] : []),
          ]}
        />
      </section>

      {isStaff && attachTx && (
        <section className="clean-panel" style={{ marginTop: 24 }}>
          {/* key remounts on a tx-switch so B's panel never flashes A's files
              (adversarial LOW) + the panel scrolls itself into view (design LOW). */}
          <TxAttachments key={attachTx.id} txId={attachTx.id} txRef={attachTx.ref}
                         onClose={() => setAttachTx(null)} />
        </section>
      )}

      <div style={{
        marginTop: 24, padding: '16px 0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: 'var(--hairline)',
      }}>
        <span className="label-s">
          SHOWING {meta.total === 0 ? 0 : page * PAGE_SIZE + 1}
          –{Math.min((page + 1) * PAGE_SIZE, meta.total)} OF {meta.total.toLocaleString()}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="filter-chip" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                  style={{ opacity: page === 0 ? 0.4 : 1, cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
            ← PREV
          </button>
          <span className="label-s" style={{ padding: '8px 14px', border: '1px solid var(--rule)' }}>
            PAGE {page + 1} / {pages}
          </span>
          <button className="filter-chip" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}
                  style={{ opacity: page >= pages - 1 ? 0.4 : 1, cursor: page >= pages - 1 ? 'not-allowed' : 'pointer' }}>
            NEXT →
          </button>
        </div>
      </div>

      <section className="clean-hide" style={{ marginTop: 64 }}>
        <div style={{ maxWidth: '76ch' }}>
          <div className="label" style={{ marginBottom: 12 }}>NOTE</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
            Every venue-side transaction is recorded against the venue's order-id or transfer-hash; cashflows applied at period close use the pending-row id. CSV export will land alongside the async export endpoint.
          </p>
        </div>
      </section>
    </div>
  );
}
